"use client"
import { VStack } from "@chakra-ui/react/stack"
import { Suspense, useEffect, useState } from "react"

import Loading from "@/app/article/[slug]/loading"
import ErrorMessage from "@/components/ErrorMessage"
import PageSkeleton from "@/components/PageSkeleton"
import Footer from "@/sections/Footer"
import SectionLatestPosts from "@/sections/LatestPosts"
import CommentsSection from "@/sections/Work/SingleArticle/CommentsSection"
import SingleContent from "@/sections/Work/SingleArticle/SingleContent"
import SingleHeader from "@/sections/Work/SingleArticle/SingleHeader"
import { Author, Category, Post, Skill } from "@/types/database"

interface WorkArticleProps {
  query: Post
  authorOnPost: Author
  categories: Category[]
  skills: Skill[]

  relatedPosts?: any[]
  likeStatus: {
    likesCount: number
    hasLiked: boolean
  }
  loading?: boolean
  articlePathPrefix?: "/article" | "/case-study"
  contentLocked?: boolean
  displayNumber?: number
}

const EMPTY_ITEMS: WorkArticleProps["relatedPosts"] = []

const WorkArticle = ({
  articlePathPrefix = "/article",
  authorOnPost,
  categories,
  contentLocked = false,
  displayNumber,
  likeStatus,
  loading = false,
  query,
  relatedPosts = EMPTY_ITEMS,
  skills,
}: WorkArticleProps) => {
  const [commentCount, setCommentCount] = useState(0)
  const [hideProtectedMeta, setHideProtectedMeta] = useState(contentLocked)

  useEffect(() => {
    setHideProtectedMeta(contentLocked)
  }, [contentLocked, query?.slug])

  return (
    <PageSkeleton loading={loading} name="work-article">
      <VStack
        bg={"bg.paper"}
        className="work-article-page"
        gap={0}
        maxW={"100%"}
        w="full"
      >
        {query ? (
          <>
            <VStack
              alignContent="stretch"
              alignItems="stretch"
              borderBottom={"1px solid {colors.border}"}
              borderRadius={"0"}
              gap={0}
              justifyContent="space-between"
              maxW={"100vw"}
              width="100%"
            >
              <SingleHeader
                allow_comments={query.allow_comments}
                articlePathPrefix={articlePathPrefix}
                articleSlug={query.slug}
                author={authorOnPost}
                categories={categories}
                client={query.client}
                commentCount={commentCount}
                date={query.date || query.date_created}
                displayNumber={displayNumber}
                endDate={query.end_date}
                headline={query.headline}
                industry={query.industry}
                likeCount={likeStatus.likesCount}
                liked={likeStatus.hasLiked}
                methodsUsed={query.methods_used}
                platform={query.platform}
                contentLocked={hideProtectedMeta}
                readingTime={query.read_time?.toString()}
                role={query.role}
                skills={skills}
                startDate={query.start_date}
                stats={query.stats}
                // likes={}
                // views={}
                team={query.team}
                thumbnail={query.image}
                title={query.title}
                titleHighlight={query.title_highlight}
                tldr={query.tldr ?? ""}
              />
            </VStack>
            <VStack
              alignContent="stretch"
              alignItems="stretch"
              borderRadius={"0"}
              className="work-article-content"
              gap={0}
              justifyContent="space-between"
              maxW={"100vw"}
              width="100%"
            >
              <SingleContent
                contentLocked={contentLocked}
                onProtectedContentUnlocked={() => setHideProtectedMeta(false)}
                post={query}
              />
            </VStack>
            {query.allow_comments !== false && (
              <Suspense fallback={<Loading />}>
                <CommentsSection
                  onCommentCountChange={setCommentCount}
                  postId={query.id}
                />
              </Suspense>
            )}
            <Suspense fallback={<Loading />}>
              <SectionLatestPosts
                data={relatedPosts}
                title="Latest Articles & Case Studies"
              />
            </Suspense>
            {/* <Suspense fallback={<Loading />}>
            <SectionLatestPosts data={latestPosts.posts} />
          </Suspense> */}
          </>
        ) : (
          <VStack
            alignItems="flex-start"
            px={{ base: 8, md: 10, lg: 24, xl: 40 }}
          >
            <ErrorMessage
              message="Please try again."
              status="error"
              title="Unable to locate the work article!"
            />
          </VStack>
        )}
        {/* <LetsTalk2 /> */}
        <Footer />
      </VStack>
    </PageSkeleton>
  )
}

export default WorkArticle
